// Task 5 (piece-move-legality-hints plan) — optimistic staged-move preview.
//
// Pure helpers extracted from MatchScreen so they are testable without pulling
// in the @pixi/react reconciler graph that MatchScreen transitively imports.

import { isMoveLegalUnderRole } from '../../game/chess-move-geometry'
import type { PlayerView, RoleKind } from '../../game/PlayerViewSchema'
import { applyStagedMove, type StagedMove } from '../../game/staged-move'
import type { PhaseBComposite } from '../../stores/turnBuilder'

/**
 * Derive the optimistic staged move from the live $turnBuilder phaseB composite.
 * The composite `move` slot is either a real `{from,to}` (staged on a completed
 * drag) or `null` (no overlay) — there is no half-staged sentinel in the
 * composite model. Guarded defensively against negative indices.
 */
export function stagedMoveFromPhaseB(phaseB: PhaseBComposite): StagedMove {
  const move = phaseB.move
  if (move === null || move.from < 0 || move.to < 0) return null
  return { from: move.from, to: move.to }
}

/** A staged empower: the cell whose piece optimistically adopts `role`. */
export type StagedEmpower = { cell_idx: number; role: RoleKind } | null

/**
 * Derive the optimistic staged empower from the live $turnBuilder phaseB
 * composite. The `empower` slot is set only once a piece is tapped (real
 * cell_idx); a `null` slot yields no overlay. (Re-empower / `extend` does not
 * change the role glyph — the piece is already that role — so it has no preview.)
 */
export function stagedEmpowerFromPhaseB(
  phaseB: PhaseBComposite,
): StagedEmpower {
  const empower = phaseB.empower
  if (empower === null || empower.cell_idx < 0) return null
  return { cell_idx: empower.cell_idx, role: empower.role }
}

/**
 * Pure overlay — return a copy of `board` where the empowered cell's `role_kind`
 * is the chosen role, so PieceLayer paints the new role glyph before the server
 * confirms. Source board is never mutated.
 */
function applyStagedEmpower(
  board: PlayerView['board'],
  staged: NonNullable<StagedEmpower>,
): PlayerView['board'] {
  return board.map((c) =>
    c.cell_idx === staged.cell_idx ? { ...c, role_kind: staged.role } : c,
  )
}

/**
 * Compute the PlayerView the CHESS view should render. With the composite chess
 * step a player may stage BOTH an empower AND a move in one turn, so the overlay
 * composes them in canonical order (D3): empower first (the piece adopts its new
 * role glyph), then the move relocates that piece — `applyStagedMove` carries
 * `role_kind` with the piece, so a freshly-empowered piece previews in its moved
 * position with the new role. Returns the unmodified `playerView` when nothing
 * is staged. The LETTERS view always keeps the real `playerView` — only the
 * chess board is overlaid.
 */
export function chessViewPlayerView(
  playerView: PlayerView | null,
  phaseB: PhaseBComposite,
): PlayerView | null {
  if (playerView === null) return null
  let board = playerView.board
  const empower = stagedEmpowerFromPhaseB(phaseB)
  if (empower !== null) {
    board = applyStagedEmpower(board, empower)
  }
  const move = stagedMoveFromPhaseB(phaseB)
  if (move !== null) {
    board = applyStagedMove(board, move)
  }
  if (board === playerView.board) return playerView
  return { ...playerView, board }
}

/**
 * ARCA-769 (ARCA-717 R2) — does the staged composite stage an EMPOWER and a
 * MOVE on the SAME piece where the move is ILLEGAL under the newly-empowered
 * role? Canonical apply order is powerup → empower → move (backend D3), so such
 * a move is validated against the post-empower role and the server 409s
 * `phase_b:illegal_move`. `your_legal_moves` reflects the PRE-empower board and
 * can't see this, so the client recomputes legality under the staged role —
 * mirroring the backend geometry (`chess-move-geometry.ts`) — to warn + block
 * Submit before the illegal combination round-trips.
 *
 * Returns false unless BOTH a move and an empower are staged on the same piece
 * AND the move is illegal under the empowered role — so there are no false
 * positives:
 *   • empowering to a role that still permits the staged move → false (no warn),
 *   • moving a piece NOT empowered this turn → false (empower.cell_idx ≠ from),
 *   • re-empower (`extend`) keeps the piece's existing role, so a move already
 *     gated by `your_legal_moves` stays legal — it can never induce a conflict
 *     and is intentionally not considered here.
 */
export function stagedMoveEmpowerConflict(
  playerView: PlayerView | null,
  phaseB: PhaseBComposite,
): boolean {
  if (playerView === null) return false
  const { move, empower } = phaseB
  if (move === null || empower === null) return false
  // Only the SAME piece: the empowered cell must be the move's origin.
  if (empower.cell_idx !== move.from) return false
  return !isMoveLegalUnderRole(
    playerView.board,
    playerView.your_slot,
    move.from,
    move.to,
    empower.role,
  )
}
